import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CreateOperationParams,
  Operation,
  Permission,
} from "@forgepoint/shared";
import { Pencil } from "lucide-react";
import { useOperationsStore } from "@/modules/operations/operationsStore";
import OperationEditorSheet from "@/modules/operations/OperationEditorSheet";
import { EntitySelector } from "@/components/EntitySelector";
import { Button } from "@/components/ui/button";
import { DataTableBadge } from "@/components/ui/data-table-badge";
import { EntityCreateRow } from "@/components/entitySelector/EntitySelectorCreateRows";
import { operationSourceLabel } from "@/components/operationSelector/operationLabels";
import { OperationRowMeta } from "@/components/operationSelector/OperationRowMeta";
import { OperationRowPrice } from "@/components/operationSelector/OperationRowPrice";
import { useRecentOperations } from "@/components/operationSelector/useRecentOperations";
import { useHasPermission } from "@/store/authStore";
import { cn } from "@/lib/utils";

const OPERATIONS_PAGE_SIZE = 50;
const POPOVER_MIN_WIDTH = 380;

interface OperationSelectorProps {
  currentlySelectedId: string | null;
  onSelectId: (operationId: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: "default" | "ghost";
  /** Shows the create row in the dropdown, subject to the operations edit permission. */
  enableQuickCreate?: boolean;
}

/**
 * Searchable operation picker showing each operation's cost, costing basis and
 * freshness, with paging, a recently used group and an inline create action.
 * @param props - The current selection, change handler and presentation options
 * @returns The operation selector and the editor sheets for creating and editing operations
 */
export function OperationSelector({
  currentlySelectedId,
  onSelectId,
  placeholder = "Select an operation...",
  className,
  disabled = false,
  variant = "default",
  enableQuickCreate = true,
}: OperationSelectorProps) {
  const { operations, listLoading, fetchOperations, fetchOperation } =
    useOperationsStore();
  const canEditOperations = useHasPermission(Permission.OPERATIONS_EDIT);
  const showCreateRows = enableQuickCreate && canEditOperations;

  const { recentOperationIds, pushRecentOperation } = useRecentOperations();

  const [loadedCount, setLoadedCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // The search the displayed rows belong to, so a late response for a superseded
  // search cannot overwrite the paging counters.
  const activeSearch = useRef<string | undefined>(undefined);
  // Recent ids already requested, so a since-deleted operation cannot loop.
  const hydratedRecentIds = useRef(new Set<string>());

  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [createSheetName, setCreateSheetName] = useState("");
  const [isEditorSheetOpen, setIsEditorSheetOpen] = useState(false);

  useEffect(() => {
    if (currentlySelectedId && !operations.has(currentlySelectedId)) {
      fetchOperation(currentlySelectedId);
    }
  }, [currentlySelectedId, fetchOperation, operations]);

  // The first page rarely covers the recently used ids, and the dropdown drops
  // any pinned id it cannot resolve, so fetch the ones the cache is missing.
  useEffect(() => {
    recentOperationIds
      .filter((id) => !operations.has(id) && !hydratedRecentIds.current.has(id))
      .forEach((id) => {
        hydratedRecentIds.current.add(id);
        fetchOperation(id);
      });
  }, [recentOperationIds, fetchOperation, operations]);

  // Recents resolve from the store, which also holds archived operations.
  const selectableRecentIds = useMemo(
    () => recentOperationIds.filter((id) => !operations.get(id)?.archivedAt),
    [recentOperationIds, operations],
  );

  const fetchEntities = useCallback(
    async (search?: string) => {
      activeSearch.current = search;
      const result = await fetchOperations({
        search,
        limit: OPERATIONS_PAGE_SIZE,
        offset: 0,
      });
      if (activeSearch.current === search) {
        setLoadedCount(result.ids.length);
        setTotal(result.total);
      }
      return result.ids;
    },
    [fetchOperations],
  );

  const handleLoadMore = useCallback(
    async (search?: string) => {
      setIsLoadingMore(true);
      try {
        const result = await fetchOperations({
          search,
          limit: OPERATIONS_PAGE_SIZE,
          offset: loadedCount,
        });
        if (activeSearch.current === search) {
          setLoadedCount((current) => current + result.ids.length);
          setTotal(result.total);
        }
        return result.ids;
      } finally {
        setIsLoadingMore(false);
      }
    },
    [fetchOperations, loadedCount],
  );

  const handleSelectId = useCallback(
    (operationId: string | null) => {
      onSelectId(operationId);
      if (operationId) {
        pushRecentOperation(operationId);
      }
    },
    [onSelectId, pushRecentOperation],
  );

  const handleCreate = useCallback((query: string, close: () => void) => {
    close();
    setCreateSheetName(query.trim());
    setIsCreateSheetOpen(true);
  }, []);

  const createRows = useMemo<EntityCreateRow[] | undefined>(() => {
    if (!showCreateRows) {
      return undefined;
    }
    return [
      {
        key: "create-operation",
        label: (query) => `Create operation "${query}"...`,
        onSelect: handleCreate,
      },
    ];
  }, [showCreateRows, handleCreate]);

  // A stable reference, so the sheet's seeding effect cannot re-run and discard
  // whatever the user has typed into the create form.
  const createSheetInitialValues = useMemo<Partial<CreateOperationParams>>(
    () => ({ name: createSheetName }),
    [createSheetName],
  );

  const handleCreated = useCallback(
    (operation: Operation) => {
      handleSelectId(operation.id);
    },
    [handleSelectId],
  );

  return (
    <>
      <div className={cn("flex w-full items-center gap-1", className)}>
        <EntitySelector<Operation>
          entities={operations}
          listLoading={listLoading && !isLoadingMore}
          fetchEntities={fetchEntities}
          getDisplayName={getOperationName}
          renderRowMeta={renderOperationMeta}
          renderRowPrice={renderOperationRowPrice}
          renderTriggerValue={renderOperationTriggerValue}
          createRows={createRows}
          onLoadMore={handleLoadMore}
          hasMore={loadedCount < total}
          isLoadingMore={isLoadingMore}
          popoverMinWidth={POPOVER_MIN_WIDTH}
          pinnedEntityIds={selectableRecentIds}
          pinnedGroupLabel="Recently used"
          currentlySelectedId={currentlySelectedId}
          onSelectId={handleSelectId}
          placeholder={placeholder}
          searchPlaceholder="Search operations..."
          disabled={disabled}
          variant={variant}
          entityLabel="operations"
          className="min-w-0 flex-1"
        />
        {currentlySelectedId && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            onClick={() => setIsEditorSheetOpen(true)}
            aria-label="Edit operation"
            title="Edit operation"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <OperationEditorSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        initialValues={createSheetInitialValues}
        onSave={handleCreated}
      />
      {currentlySelectedId && (
        <OperationEditorSheet
          open={isEditorSheetOpen}
          onOpenChange={setIsEditorSheetOpen}
          operationId={currentlySelectedId}
          readOnly={!canEditOperations}
        />
      )}
    </>
  );
}

/**
 * Reads an operation's display name.
 * @param operation - The operation to label
 * @returns The operation name
 */
function getOperationName(operation: Operation): string {
  return operation.name;
}

/**
 * Renders the secondary line of an operation row.
 * @param operation - The operation to describe
 * @returns The source type and freshness line
 */
function renderOperationMeta(operation: Operation) {
  return <OperationRowMeta operation={operation} />;
}

/**
 * Renders the right-aligned cost block of an operation row.
 * @param operation - The operation to price
 * @returns The stacked cost and basis
 */
function renderOperationRowPrice(operation: Operation) {
  return <OperationRowPrice operation={operation} variant="row" />;
}

/**
 * Renders the trailing source type and cost on the closed trigger, marking an
 * archived selection.
 * @param operation - The operation to describe
 * @returns The single-line source type, cost and basis
 */
function renderOperationTriggerValue(operation: Operation) {
  return (
    <span className="whitespace-nowrap">
      {operation.archivedAt && (
        <DataTableBadge color="amber" className="mr-2">
          Archived
        </DataTableBadge>
      )}
      <span className="text-muted-foreground">
        {operationSourceLabel(operation)} ·{" "}
      </span>
      <OperationRowPrice operation={operation} variant="trigger" />
    </span>
  );
}
